import "./i18n/config";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";

import OnboardingScreen from "./components/OnboardingScreen";
import { ScrollToTop } from "./components/ScrollToTop";
import { getPostAuthRedirectPath } from "./hooks/useAuth";
import MainLayout from "./layout/MainLayout";
// Pages
import ContactUsPage from "./pages/ContactUsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ImposterPage from "./pages/ImposterPage";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import PartyDetailPage from "./pages/party/PartyDetailPage";
import PartyCastRevealPage from "./pages/party/PartyCastRevealPage";
import PartyJoinBluffPage from "./pages/party/PartyJoinBluffPage";
import PartyJoinImposterOnlinePage from "./pages/party/PartyJoinImposterOnlinePage";
import PartyModesPage from "./pages/party/PartyModesPage";
import PartyPlayPage from "./pages/party/PartyPlayPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import PurchasePage from "./pages/PurchasePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SignUpPage from "./pages/SignUpPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";
import UpdateProfilePage from "./pages/UpdateProfilePage";
import PartyLayout from "./party/PartyLayout";
import { QueryProvider } from "./providers/QueryProvider";
import theme from "./theme/theme";
import { apiClient } from "./utils/api";
import { hasCompletedOnboarding } from "./utils/onboardingCompleted";
import { MixpanelEvents, trackEvent } from "./utils/mixpanel";

/** Records auth-gate funnel then redirects to login with ?next=. */
const RedirectToLogin: React.FC<{ to: string; intendedPath: string }> = ({
  to,
  intendedPath,
}) => {
  useEffect(() => {
    trackEvent(MixpanelEvents.AUTH_GATE_REDIRECT, {
      intended_path:
        intendedPath.length > 180 ? `${intendedPath.slice(0, 180)}…` : intendedPath,
    });
  }, [intendedPath]);
  return <Navigate to={to} replace />;
};

// Protected Route Component - Requires authentication
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");

  // Require a refreshable session (access or refresh token), not user_data alone
  if (accessToken || refreshToken) {
    return <>{children}</>;
  }

  const next = `${location.pathname}${location.search || ""}`;
  const loginTo = `/login?next=${encodeURIComponent(next)}`;
  return <RedirectToLogin to={loginTo} intendedPath={next} />;
};

// Public Route Component — redirects to post-auth home if already logged in
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Only check for access token to avoid redirect loops with stale/invalid tokens
  // Access token is the most reliable indicator of being logged in
  const accessToken = localStorage.getItem("access_token");
  const userData = apiClient.getUserData();

  // Only redirect if we have both access token and user data
  // This prevents redirect loops when tokens are stale or invalid
  if (accessToken && userData) {
    return <Navigate to={getPostAuthRedirectPath()} replace />;
  }

  return <>{children}</>;
};

function App() {
  const { i18n } = useTranslation();
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Set document direction based on language
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language || "ar";
  }, [i18n.language]);

  useEffect(() => {
    // Don't show onboarding if route has payment status (payment callback)
    // Check URL params directly since we're outside Router context
    const currentPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get("paymentStatus");
    const onboardingCompleted = hasCompletedOnboarding();

    // Check if onboarding has been completed on this device (localStorage)
    if (!onboardingCompleted && !paymentStatus && currentPath === "/") {
      setShowOnboarding(true);
    }

    if (currentPath !== "/" || paymentStatus) {
      setShowOnboarding(false);
    }


    // Restore login session on app load
    const restoreSession = async () => {
      const refreshToken = localStorage.getItem("refresh_token");
      const accessToken = localStorage.getItem("access_token");
      const userData = apiClient.getUserData();

      // If we have tokens or user data, try to restore session
      if (refreshToken || accessToken || userData) {
        // If we have a refresh token but no access token, try to refresh
        if (refreshToken && !accessToken) {
          try {
            await apiClient.refreshToken();
            // User data should persist, tokens are refreshed
          } catch (error) {
            // Refresh failed, clear everything
            apiClient.logout();
          }
        }
        // If we have access token, user data should be available
        // If user data is missing but we have tokens, it will be fetched by useUser hook
      } else {
        // No tokens or user data, ensure everything is cleared
        apiClient.logout();
      }

      setIsRestoringSession(false);
    };

    restoreSession();
  }, []);

  // Show loading state while restoring session
  if (isRestoringSession) {
    return (
      <QueryProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            Loading...
          </div>
        </ThemeProvider>
      </QueryProvider>
    );
  }

  // Show onboarding screen if needed
  if (showOnboarding) {
    return (
      <QueryProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <OnboardingScreen onComplete={() => setShowOnboarding(false)} />
        </ThemeProvider>
      </QueryProvider>
    );
  }

  return (
    <QueryProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <HelmetProvider>
            <ScrollToTop />
            <Routes>
              <Route element={<MainLayout />}>
              <Route index element={<LandingPage />} />
              <Route path="home" element={<Navigate to="/" replace />} />

              <Route path="party/join/bluff" element={<PartyJoinBluffPage />} />
              <Route path="party/join/imposter" element={<PartyJoinImposterOnlinePage />} />

              <Route path="party" element={<PartyLayout />}>
                <Route index element={<Navigate to="/" replace />} />
                <Route path="cast/:shellId" element={<PartyCastRevealPage />} />
                <Route path="modes" element={<PartyModesPage />} />
                <Route path="detail/:shellId" element={<PartyDetailPage />} />
                <Route path="shop" element={<Navigate to="/purchase" replace />} />
                <Route
                  path="play/:shellId"
                  element={
                    <ProtectedRoute>
                      <PartyPlayPage />
                    </ProtectedRoute>
                  }
                />
              </Route>


              <Route
                path="signup"
                element={
                  <PublicRoute>
                    <SignUpPage />
                  </PublicRoute>
                }
              />
              <Route
                path="login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPasswordPage />
                  </PublicRoute>
                }
              />
              <Route
                path="reset-password/:token"
                element={
                  <PublicRoute>
                    <ResetPasswordPage />
                  </PublicRoute>
                }
              />


              {/* Imposter route is unprotected - accessible to anyone */}
              <Route path="imposter/:identifier" element={<ImposterPage />} />

              {/* Legacy catalog / session URLs → Party home (old flows removed) */}
              <Route path="library" element={<Navigate to="/" replace />} />
              <Route path="games" element={<Navigate to="/" replace />} />
              <Route path="game/:slug" element={<Navigate to="/party/modes" replace />} />
              <Route path="create-game" element={<Navigate to="/party/modes" replace />} />
              <Route path="create-game/:slug" element={<Navigate to="/party/modes" replace />} />
              <Route path="team-setup" element={<Navigate to="/" replace />} />
              <Route path="game-board" element={<Navigate to="/" replace />} />
              <Route path="content/:modeCategoryIndex/:contentIndex" element={<Navigate to="/" replace />} />
              <Route path="winning" element={<Navigate to="/" replace />} />
              <Route
                path="purchase"
                element={
                  <ProtectedRoute>
                    <PurchasePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="update-profile"
                element={
                  <ProtectedRoute>
                    <UpdateProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="change-password"
                element={
                  <ProtectedRoute>
                    <Navigate to="/update-profile?tab=1" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="contact-us"
                element={<ContactUsPage />}
              />
              <Route
                path="privacy-policy"
                element={<PrivacyPolicyPage />}
              />
              <Route
                path="terms-and-conditions"
                element={<TermsAndConditionsPage />}
              />
              {/* Legacy routes for backward compatibility - redirect to /auth routes
              <Route
                path="/signup"
                element={<Navigate to="/auth/sign-up" replace />}
              />
              <Route
                path="/login"
                element={<Navigate to="/auth/sign-in" replace />}
              />
              <Route
                path="/forgot-password"
                element={<Navigate to="/auth/forgot-password" replace />}
              />
              <Route
                path="/reset-password"
                element={<Navigate to="/auth/reset-password" replace />}
              /> */}
              </Route>
            </Routes>
          </HelmetProvider>
        </Router>
      </ThemeProvider>
    </QueryProvider>
  );
}

export default App;
